/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import React, { Fragment, useMemo, useState } from 'react';
import { ThemedStyleFn, useStyles } from '~src/hooks/useStyles';
import { useIntl } from 'react-intl';
import { Button, Typography, Tooltip } from 'antd';
import moment from 'moment';
import IconButton from '~components/_common/IconButton/IconButton';
import { QaSettingsModal } from '~components/popups-modals/TenderMessagesModal/QaSettingsModal';

export type qaType = {
  qaPeriodStart?: number;
  qaPeriodEnd?: number;
  qaDaysLeft?: number;
  qaOn: boolean;
  qaActiveFrom: number;
  qaDeadlineAt: number;
  registrationState?: number;
  onDaysLeftChange?: (daysLeft: number) => void;
  isVendor?: boolean;
  onAddQuestion?: () => void;
  projectId?: string;
  tenderId?: string;
  tenderData?: any;
};

const STATE_COMPLETED = 6;

const QuestionsAnswerDateBlock: React.FC<qaType> = ({
  qaPeriodStart,
  qaPeriodEnd,
  qaDeadlineAt,
  qaOn,
  registrationState,
  isVendor = false,
  onAddQuestion,
  projectId,
  tenderId,
  tenderData,
}) => {
  const intl = useIntl();
  const styles = useStyles(styleFn);
  const locale = intl.locale === 'da' ? 'da' : 'en';

  // settings modal
  const [isModalVisible, setIsModalVisible] = useState(false);

  // --- 1. End Date Logic (Moved up so useMemo can use it) ---
  // qa_stop takes precedence, else deadline_at
  const endDateValue =
    qaPeriodEnd && qaPeriodEnd > 0
      ? qaPeriodEnd
      : qaDeadlineAt && qaDeadlineAt > 0
      ? qaDeadlineAt
      : null;

  // --- 2. Days Left Logic (Moved up to fix Hook error) ---
  // This must be called before any return statements
  const daysLeftCalculated = useMemo(() => {
    if (!endDateValue) return 0;
    const now = moment();
    const end = moment.unix(endDateValue);
    const diffInSeconds = end.diff(now, 'seconds');

    if (diffInSeconds <= 0) return 0;
    const days = Math.floor(diffInSeconds / 86400);
    return days;
  }, [endDateValue]);

  // --- 3. Container Logic ---
  // Hide if Completed (state >= 6) OR QA feature is turned off entirely
  const isTenderClosed =
    !!registrationState && registrationState >= STATE_COMPLETED;

  const isQaOff = !qaOn;

  const isQaClosed = isQaOff || isTenderClosed;

  // --- 4. Start Date Logic ---
  const startDateValue =
    qaPeriodStart && qaPeriodStart > 0 ? qaPeriodStart : null;

  // Is the current time BEFORE the start date?
  const isStartFuture = startDateValue
    ? moment.unix(startDateValue).isAfter(moment())
    : false;

  // Show logic: Start date exists AND it is in the future
  const showStartSection = !!startDateValue && isStartFuture;

  // --- 5. End/Ended Logic ---
  const showEndSection = !!endDateValue;

  // Is the current time AFTER the end date?
  const isEnded = endDateValue
    ? moment.unix(endDateValue).isBefore(moment())
    : false;

  // --- 6. Button Disable Logic ---
  // Disabled if: Time is before start OR Time is after end
  const isButtonDisabled = isStartFuture || isEnded || isQaClosed;

  // --- Formatters ---
  const formatDate = (unix: number) =>
    moment
      .unix(unix)
      .locale(locale)
      .format(locale === 'da' ? 'D. MMMM YYYY HH:mm' : 'MMMM D. YYYY HH:mm');

  const headerNode = (() => {
    // 1) Tender closed: must be in main title (same style as other QA fonts)
    if (isTenderClosed) {
      return (
        <Typography css={styles.rightTitle}>
          {intl.formatMessage({
            id: 'qa-disabled-tender-closed-title',
            defaultMessage: 'Tender is closed, Q&A is disabled',
          })}
        </Typography>
      );
    }

    // 2) Tender not closed, but qaOn is false: show disabled title
    if (isQaOff && !isTenderClosed) {
      return (
        <Typography css={styles.rightTitle}>
          {intl.formatMessage({
            id: 'qa-disabled-title',
          })}
        </Typography>
      );
    }

    // Default (QA ON): keep original behavior
    return (
      <Typography css={styles.rightTitle}>
        {intl.formatMessage({
          id: isEnded ? 'qa-has-ended' : 'qa-header-details',
        })}
      </Typography>
    );
  })();

  return (
    <div css={styles.qaContainer}>
      {/* Header: Changes text based on Ended state */}
      {headerNode}

      {/* Disabled state: still show container + help message */}
      {isQaClosed ? (
        <div css={styles.sectionBlock}>
          {/* show help ONLY if tender NOT closed and QA is OFF */}
          {!isTenderClosed && isQaOff && (
            <Fragment>
              <span css={styles.label}>
                {intl.formatMessage({
                  id: 'qa-disabled-info',
                })}
              </span>
              <span css={styles.label}>
                {intl.formatMessage({
                  id: isVendor
                    ? 'qa-disabled-help-vendor'
                    : 'qa-disabled-help-project',
                })}
              </span>
              {!isVendor && (
                <Tooltip
                  title={intl.formatMessage({
                    id: 'edit',
                    defaultMessage: 'Edit Settings',
                  })}>
                  <Button
                    type="text"
                    size="small"
                    onClick={() => setIsModalVisible(true)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}>
                    <IconButton icon={['far', 'cog']} backgroundOff />
                    {intl.formatMessage({ id: 'edit' })}
                  </Button>
                </Tooltip>
              )}
            </Fragment>
          )}
        </div>
      ) : (
        <Fragment>
          {/* Start Date Section: Hidden if ended or started */}
          {!isEnded && showStartSection && (
            <div css={styles.sectionBlock}>
              <Typography css={styles.label}>
                {intl.formatMessage({ id: 'qa-start-time' })}
              </Typography>
              <Typography css={styles.dateLine}>
                {formatDate(startDateValue!)}
              </Typography>
            </div>
          )}

          {/* End Date Section */}
          {showEndSection && (
            <div css={styles.sectionBlock}>
              {/* If ended, hide the label to match design, otherwise show it */}
              {!isEnded && (
                <Typography css={styles.label}>
                  {intl.formatMessage({ id: 'qa-end-time' })}
                </Typography>
              )}

              <Typography css={styles.dateLine}>
                {formatDate(endDateValue!)}
              </Typography>

              {/* Days Left: Only show if NOT ended and days > 0 */}
              {!isStartFuture && !isEnded && daysLeftCalculated > 0 && (
                <Typography css={styles.rightTimeLeft}>
                  {intl.formatMessage({ id: 'days-left' })}:{' '}
                  {daysLeftCalculated}
                </Typography>
              )}
            </div>
          )}

          {/* Vendor Button */}
          {isVendor && (
            <div
              css={{
                display: 'flex',
                width: '100%',
                justifyContent: 'flex-end',
                marginTop: '15px',
              }}>
              <Button
                onClick={onAddQuestion}
                disabled={isButtonDisabled}
                css={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}>
                <IconButton
                  icon={['far', 'pen-line']}
                  containerStyle={{
                    marginLeft: 0,
                    color: isButtonDisabled ? 'grey' : '#4170ff',
                  }}
                  backgroundOff
                  widthTooltip="300px"
                />
                {intl.formatMessage({ id: 'new-question' })}
              </Button>
            </div>
          )}
        </Fragment>
      )}

      {/* New Settings Modal */}
      <QaSettingsModal
        visible={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        onOk={() => setIsModalVisible(false)}
        projectId={projectId}
        tenderId={tenderId}
        tenderData={tenderData}
      />
    </div>
  );
};

export default QuestionsAnswerDateBlock;

export const styleFn: ThemedStyleFn = (theme) => ({
  qaContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    textAlign: 'right',
    width: '100%',
    gap: '20px',
  },
  rightTitle: {
    fontSize: '14px',
    fontWeight: 800,
    whiteSpace: 'normal',
    marginBottom: '10px',
  },
  sectionBlock: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: '5px',
  },
  label: {
    fontSize: '12px',
    color: 'grey',
    fontWeight: 500,
    whiteSpace: 'normal',
  },
  dateLine: {
    fontSize: '16px',
    fontWeight: 600,
    whiteSpace: 'normal',
  },
  rightTimeLeft: {
    color: '#1890ff',
    fontSize: '14px',
    fontWeight: 500,
    marginTop: '5px',
  },
});
